/**
 * Dashboard prefetch entry.
 *
 * A single `/quickfetch/dashboard/` HTTP call feeds every above-the-fold
 * dashboard cache: we capture the apiGet promise once and derive all seeds
 * from it.
 *
 * ## The two seeding strategies (deliberate — don't "unify" them)
 *
 * 1. **Synchronous `fetchQuery` registered BEFORE the await** (proposals,
 *    meetings, reminders, tasks, onboarding status, import status, reminder
 *    automation): registers an in-flight query under the consumer's exact key,
 *    so a hook mounting mid-flight ADOPTS the pending promise instead of
 *    firing its own request. Required for caches whose consumer can mount
 *    while quickfetch is still in flight — which is every cache the fold's
 *    first paint depends on. (Meetings can register pre-await when persisted
 *    entity IDs are available; post-await otherwise — dual registration is
 *    deliberate defense-in-depth for stale persisted data.)
 *
 * 2. **Post-await `setQueryData`** (integrations list, onboarding actions,
 *    feature flags): a plain cache write once the data is in hand. No
 *    in-flight dedup — acceptable for these because their consumers read the
 *    cache after the prefetch completes (or poll), and a rare mid-flight mount
 *    just falls back to a normal fetch. See dashboardSecondarySeeds.ts.
 *
 * If you add a seed for a cache whose consumer mounts during the in-flight
 * window, use strategy 1.
 *
 * ## The day window
 *
 * Meetings, reminders and tasks are "today's", and today depends on the
 * user's profile timezone. On a revisit that zone is persisted, so the client
 * computes the window and sends it. On a cold first visit it is not — the
 * client sends NO window and the server derives it from the profile, then
 * reports the zone it used (`timezone_used`). The post-await seeds rebuild
 * their keys from that zone, so they match the live hook the moment the
 * profile loads, instead of guessing a zone and fetching the wrong day.
 *
 * ## The persisted fold
 *
 * Once the response is in hand it is also persisted (persistedDashboardFold)
 * so the NEXT visit paints it before any request completes — see
 * hydratePersistedQueries.ts.
 */

import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { hashKey } from '@tanstack/react-query';

import { REMINDER_AUTOMATION_QUERY_KEY } from '@/hooks/useReminderAutomationEnabled';
import { mark } from '@/lib/boot/bootMetrics';
import { getStoreGeneration, upsertListResponseToStore } from '@/lib/crm/store';
import {
  type HiddenMeetings,
  hiddenMeetingsQueryKey,
  seedEmptyHiddenMeetingsIfUnseeded,
} from '@/lib/dashboard/hiddenMeetings';
import {
  todayMeetingsQueryOptions,
  todayRemindersQueryOptions,
  todayTasksQueryOptions,
} from '@/lib/dashboard/queries';
import {
  buildTodayMeetingsFilters,
  getDashboardDateRange,
  getDashboardReminderDateRange,
} from '@/lib/dates/dashboardDateRange';
import { apiGet } from '@/lib/http/client';
import { setPersistedDashboardFold } from '@/lib/localStorage/persistedDashboardFold';
import {
  getPersistedDashboardPrefetch,
  setPersistedDashboardPrefetch,
} from '@/lib/localStorage/persistedDashboardPrefetch';
import { getPersistedDisplayTimezone } from '@/lib/localStorage/persistedUserProfile';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
// Direct module import (not the `@/lib/time` barrel) — see the comment on the
// equivalent import in dashboardDateRange.ts for why: the barrel re-exports
// useOrgTimeFormat, which imports OrgContext.tsx, and this module is itself
// reached from OrgContext.tsx (via bootRequests.ts), so importing the barrel
// here closes an import cycle back into OrgContext.tsx.
import { todayIn } from '@/lib/time/calendarDay';
import { LOADING_FALLBACK_TIMEZONE } from '@/lib/time/loadingFallbackTimezone';
import type { CrmObjectsListResponse } from '@/types/crm';

import { prefetchDebug } from '../debug';
import type { CombinedPrefetchEntry } from '../types';
import type { DashboardQuickfetchResponse } from './dashboardQuickfetchTypes';
import { seedSecondaryDashboardCaches } from './dashboardSecondarySeeds';

/** The "today" bounds every day-scoped dashboard query is keyed on. */
export interface DashboardDayWindow {
  rangeStartISO: string;
  rangeEndISO: string;
  reminderStartDate: string;
  reminderEndDate: string;
}

/**
 * Today's window in `timeZone`, from the SAME builders the live hooks use
 * (getDashboardDateRange / getDashboardReminderDateRange) so keys match
 * byte-for-byte.
 */
export function dashboardDayWindowFor(timeZone: string): DashboardDayWindow {
  const { rangeStartISO, rangeEndISO } = getDashboardDateRange(timeZone);
  // Reminders filter on a date-only field in the user's LOCAL timezone —
  // computed differently from the UTC meetings range above.
  const { todayDateOnly, tomorrowDateOnly } =
    getDashboardReminderDateRange(timeZone);
  return {
    rangeStartISO,
    rangeEndISO,
    reminderStartDate: todayDateOnly,
    reminderEndDate: tomorrowDateOnly,
  };
}

/** The proposals list key the dashboard's fast query and this seed share. */
export function dashboardProposalsQueryKey(orgId: number) {
  return proposalQueryKeys.list(orgId, {
    resolution: 'pending',
    assigned_to_me: true,
    page: 1,
    page_size: 10,
  });
}

/** The import-status cache useImportStatus reads (orgId as a string there). */
export function importStatusQueryKey(orgId: number) {
  return ['import-status', String(orgId)] as const;
}

/** The reminder-automation cache useReminderAutomationEnabled reads. */
export function reminderAutomationQueryKey(orgId: number) {
  return ['crm', orgId, REMINDER_AUTOMATION_QUERY_KEY] as const;
}

/**
 * Shape useImportStatus's queryFn returns — snake_case provider/job payload
 * transformed into camelCase service entries (jobs stay snake_case because
 * they pass straight through to the zod ImportJobSchema).
 */
export function toImportStatusFetchResult(
  importJobs: NonNullable<DashboardQuickfetchResponse['import_jobs']>
) {
  return {
    services: importJobs.services.map(svc => ({
      providerName: svc.provider_name,
      displayName: svc.display_name,
      kind: svc.kind,
      jobs: svc.jobs,
    })),
    error: null,
  };
}

function requireList(
  list: CrmObjectsListResponse | null | undefined,
  name: string
): CrmObjectsListResponse {
  if (!list?.results || !Array.isArray(list.results)) {
    throw new Error(`Malformed quickfetch response: missing ${name} data`);
  }
  return list;
}

/** Orgs whose entry has run (or is running) in this page's lifetime. */
const executedOrgIds = new Set<number>();

/**
 * Run the dashboard entry for `orgId` unless it already ran this page load.
 * The prefetch script runs it on a hard load with an org in the URL; the
 * dashboard page calls this so a client-side arrival (from `/`, the org
 * switcher, a redirect) gets the same warm caches instead of cold ones.
 */
export function ensureDashboardPrefetch(
  orgId: number,
  queryClient: QueryClient
): void {
  if (executedOrgIds.has(orgId)) {
    return;
  }
  void dashboardEntry.execute(orgId, queryClient);
}

/** Test-only — forget which orgs ran. */
export function resetDashboardPrefetchForTest(): void {
  executedOrgIds.clear();
}

export const dashboardEntry: CombinedPrefetchEntry = {
  type: 'combined',
  execute: async (orgId: number, queryClient: QueryClient) => {
    executedOrgIds.add(orgId);
    try {
      // The zone the client knows before React mounts — the persisted profile
      // on a single-user device, else null. Keys are built under the fallback
      // zone in that case ONLY so hooks that mount under the same fallback can
      // adopt the in-flight promise; the fetch itself is not scoped by it (the
      // server derives the day from the profile when no window is sent).
      const persistedTimeZone = getPersistedDisplayTimezone();
      const displayTimeZone = persistedTimeZone ?? LOADING_FALLBACK_TIMEZONE;
      const window = dashboardDayWindowFor(displayTimeZone);
      const { rangeStartISO, rangeEndISO, reminderStartDate, reminderEndDate } =
        window;
      prefetchDebug('dashboard execute start', {
        orgId,
        persistedTimeZone,
        rangeStartISO,
        rangeEndISO,
        queryClientId: (queryClient as unknown as Record<string, unknown>)
          .__debugId,
        cacheSize: queryClient.getQueryCache().getAll().length,
      });

      const params = persistedTimeZone
        ? `?tz=${encodeURIComponent(persistedTimeZone)}&range_start=${rangeStartISO}&range_end=${rangeEndISO}&reminder_start_date=${reminderStartDate}&reminder_end_date=${reminderEndDate}`
        : '';

      // Fire the single backend request once. Every fetchQuery below chains
      // off this shared promise — only one HTTP call happens.
      const shared = apiGet<DashboardQuickfetchResponse>(
        `/api/orgs/${orgId}/quickfetch/dashboard/${params}`
      );
      mark('quickfetch:request', { windowSent: persistedTimeZone !== null });
      // Snapshot the store generation now (before any await) so all list
      // queryFns guard against a mid-flight org switch. clearAll() bumps the
      // generation on org change, so bodies written with a stale generation
      // are silently ignored by upsertListResponseToStore.
      const prefetchGeneration = getStoreGeneration();

      // Prevent unhandled rejection if the shared promise rejects before
      // any fetchQuery consumer or the outer `await` attaches a handler.
      // Sentry reporting is handled by the outer catch block only.
      shared.catch(() => {});

      // Every strategy-1 seed, so execute can wait for all of them and keep
      // the "prefetch complete = caches warm" contract.
      const seeds: Promise<unknown>[] = [];
      const seed = <T>(
        queryKey: readonly unknown[],
        queryFn: () => Promise<T>
      ): void => {
        seeds.push(
          queryClient
            // Disable retry so a failed quickfetch immediately falls through
            // to the hook's own fetch — otherwise React Query retries for ~7s
            // with the cache in error state, which makes isLoading=false and
            // the dashboard renders empty.
            .fetchQuery({ queryKey, retry: false, queryFn })
            .catch(() => {
              // Errors captured by the outer catch block / consumer refetch.
            })
        );
      };

      // Proposals — registered SYNCHRONOUSLY (before any await) so hooks
      // mounting on the next tick see fetchStatus: 'fetching' under this key
      // and subscribe to the same promise.
      const proposalsKey = dashboardProposalsQueryKey(orgId);
      prefetchDebug('registering proposals fetchQuery', {
        proposalsKey,
        proposalsKeyHash: hashKey(proposalsKey),
      });
      seed(proposalsKey, async () => {
        const r = await shared;
        if (!r.proposals?.results || !Array.isArray(r.proposals.results)) {
          throw new Error(
            'Malformed quickfetch response: missing proposals data'
          );
        }
        return r.proposals;
      });

      // Read persisted metadata now (before the await) so the reminders,
      // tasks and meetings pre-await registrations can build keys identical
      // to the hook's live keys.
      const persisted = getPersistedDashboardPrefetch(orgId);

      // Reminders — key includes the userEntityId (like meetings); use the
      // persisted value so mid-flight hooks adopt this promise. On a first
      // visit both pre- and post-await keys use null.
      seed(
        todayRemindersQueryOptions(
          orgId,
          reminderStartDate,
          reminderEndDate,
          persisted?.userEntityId ?? null
        ).queryKey,
        async () =>
          upsertListResponseToStore(
            requireList((await shared).reminders, 'reminders'),
            prefetchGeneration
          )
      );

      // Tasks — same day window, no per-user key input.
      seed(
        todayTasksQueryOptions(
          orgId,
          reminderStartDate,
          reminderEndDate,
          rangeStartISO,
          rangeEndISO
        ).queryKey,
        async () =>
          upsertListResponseToStore(
            requireList((await shared).tasks, 'tasks'),
            prefetchGeneration
          )
      );

      // Meetings — pre-await using persisted entity IDs from a prior visit.
      // If present and unchanged, hooks mounting mid-flight adopt this
      // promise. If stale, the post-await fetchQuery below populates the
      // fresh key as defense-in-depth.
      if (persisted) {
        const persistedMeetingsQuery = todayMeetingsQueryOptions(
          orgId,
          buildTodayMeetingsFilters(
            rangeStartISO,
            rangeEndISO,
            persisted.integrationEntityIds,
            persisted.userEntityId,
            persisted.hiddenMeetings.seriesIds,
            persisted.hiddenMeetings.meetingEntityIds
          )
        );
        prefetchDebug('registering pre-await meetings fetchQuery (persisted)', {
          persistedMeetingsKeyHash: hashKey(persistedMeetingsQuery.queryKey),
          userEntityId: persisted.userEntityId,
          integrationCount: persisted.integrationEntityIds.length,
        });
        seed(persistedMeetingsQuery.queryKey, async () =>
          upsertListResponseToStore(
            requireList((await shared).meetings, 'meetings'),
            prefetchGeneration
          )
        );
      }

      // Onboarding status and import status both gate the WHOLE page's
      // loading state (deriveDashboardState), so a mid-flight mount must
      // adopt these promises rather than fire its own requests.
      seed(['onboarding-status', orgId], async () => {
        const r = await shared;
        if (!r.onboarding_status) {
          throw new Error(
            'Malformed quickfetch response: missing onboarding_status data'
          );
        }
        return r.onboarding_status;
      });
      seed(importStatusQueryKey(orgId), async () => {
        const r = await shared;
        if (!r.import_jobs) {
          throw new Error(
            'Malformed quickfetch response: missing import_jobs data'
          );
        }
        return toImportStatusFetchResult(r.import_jobs);
      });

      // Reminder automation flag — one fewer request on the critical path.
      seed(reminderAutomationQueryKey(orgId), async () => {
        const r = await shared;
        if (typeof r.reminder_automation_enabled !== 'boolean') {
          throw new Error(
            'Malformed quickfetch response: missing reminder_automation_enabled'
          );
        }
        return r.reminder_automation_enabled;
      });

      // Await response to learn user_entity_id and the zone the server used.
      const response = await shared;
      mark('quickfetch:response', {
        proposals: response.proposals?.results?.length ?? null,
        meetings: response.meetings?.results?.length ?? null,
        reminders: response.reminders?.results?.length ?? null,
        tasks: response.tasks?.results?.length ?? null,
        timezoneUsed: response.timezone_used ?? null,
      });
      prefetchDebug('shared promise resolved', {
        userEntityId: response.user_entity_id,
        proposalsCount: response.proposals?.count,
        meetingsCount: response.meetings?.results?.length,
        timezoneUsed: response.timezone_used,
      });

      // The zone the data is actually for. Equal to displayTimeZone whenever
      // the client sent a window; on a first visit it is the profile zone the
      // server derived, and the live hook will key on that same zone once the
      // profile loads — so the post-await seeds are keyed on it.
      const resolvedTimeZone = response.timezone_used ?? displayTimeZone;
      const resolvedWindow =
        resolvedTimeZone === displayTimeZone
          ? window
          : dashboardDayWindowFor(resolvedTimeZone);

      const integrationEntityIds = response.integration_entity_ids ?? [];
      const userEntityId = response.user_entity_id ?? null;

      // The current user's hidden-meeting set, lifted to the top of the
      // response. `null` (or a malformed shape) means the backend meetings
      // fetch failed — treat as NOT loaded: don't seed/persist/register a
      // query keyed on a hidden set that's empty only because of a failure.
      const rawHidden = response.hidden_meetings;
      const hiddenLoaded =
        rawHidden != null &&
        Array.isArray(rawHidden.series_ids) &&
        Array.isArray(rawHidden.meeting_entity_ids);

      if (hiddenLoaded) {
        const hiddenMeetings: HiddenMeetings = {
          seriesIds: rawHidden.series_ids,
          meetingEntityIds: rawHidden.meeting_entity_ids,
        };
        // Seed the cache useHiddenMeetings reads, so useCalendarEvents builds its
        // filter groups (and useDismissMeeting seeds new keys) from the current
        // server-confirmed hidden set rather than an empty default.
        queryClient.setQueryData<HiddenMeetings>(
          hiddenMeetingsQueryKey(orgId),
          hiddenMeetings
        );

        // Persist the metadata for the NEXT visit's pre-await registrations.
        // setPersistedCache deduplicates identical writes, so no churn on revisits.
        if (Array.isArray(response.integration_entity_ids)) {
          setPersistedDashboardPrefetch(orgId, {
            userEntityId,
            integrationEntityIds: response.integration_entity_ids,
            hiddenMeetings,
          });
        }

        // Options come from the shared builder, guaranteeing a key byte-identical
        // to useCalendarEvents' meetings query.
        const meetingsQuery = todayMeetingsQueryOptions(
          orgId,
          buildTodayMeetingsFilters(
            resolvedWindow.rangeStartISO,
            resolvedWindow.rangeEndISO,
            integrationEntityIds,
            userEntityId,
            hiddenMeetings.seriesIds,
            hiddenMeetings.meetingEntityIds
          )
        );
        prefetchDebug('registering meetings fetchQuery', {
          meetingsKeyHash: hashKey(meetingsQuery.queryKey),
        });
        seed(meetingsQuery.queryKey, async () =>
          upsertListResponseToStore(
            requireList((await shared).meetings, 'meetings'),
            prefetchGeneration
          )
        );

        // Persist the fold so the next visit paints before any request
        // completes (hydratePersistedQueries). Only when the hidden set is
        // authoritative — a failed meetings fetch must not become tomorrow's
        // first paint.
        setPersistedDashboardFold(orgId, {
          userEntityId,
          timezone: resolvedTimeZone,
          dayKey: todayIn(resolvedTimeZone),
          rangeStartISO: resolvedWindow.rangeStartISO,
          rangeEndISO: resolvedWindow.rangeEndISO,
          reminderStartDate: resolvedWindow.reminderStartDate,
          reminderEndDate: resolvedWindow.reminderEndDate,
          integrationEntityIds,
          hiddenMeetings,
          proposals: response.proposals,
          meetings: response.meetings,
          reminders: response.reminders,
          tasks: response.tasks,
          onboardingStatus: response.onboarding_status,
          importJobs: response.import_jobs,
          featureFlags: response.feature_flags,
          reminderAutomationEnabled: response.reminder_automation_enabled,
        });
      } else {
        // Backend meetings fetch failed (hidden_meetings null/malformed).
        // Fail open with an EMPTY hidden set so the meetings gate resolves and
        // the live query can fetch — but never persist it, and never register
        // a meetings query under the failure-derived key (both handled by NOT
        // running the hiddenLoaded branch above).
        seedEmptyHiddenMeetingsIfUnseeded(queryClient, orgId);
      }

      // Post-await reminders and tasks re-registration under the fresh
      // user_entity_id and the resolved zone. Mirrors the post-await meetings
      // registration: on first visits the pre-await keys used null / the
      // fallback zone; these use the real values the hook will key on.
      seed(
        todayRemindersQueryOptions(
          orgId,
          resolvedWindow.reminderStartDate,
          resolvedWindow.reminderEndDate,
          userEntityId
        ).queryKey,
        async () =>
          upsertListResponseToStore(
            requireList((await shared).reminders, 'reminders'),
            prefetchGeneration
          )
      );
      if (resolvedTimeZone !== displayTimeZone) {
        seed(
          todayTasksQueryOptions(
            orgId,
            resolvedWindow.reminderStartDate,
            resolvedWindow.reminderEndDate,
            resolvedWindow.rangeStartISO,
            resolvedWindow.rangeEndISO
          ).queryKey,
          async () =>
            upsertListResponseToStore(
              requireList((await shared).tasks, 'tasks'),
              prefetchGeneration
            )
        );
      }

      // Seed the integration list, onboarding-tagged proposals, and feature
      // flags caches — see dashboardSecondarySeeds.ts.
      seedSecondaryDashboardCaches(response, queryClient, orgId);

      // Every strategy-1 seed settled — "prefetch complete = caches warm".
      await Promise.all(seeds);
      mark('prefetch:complete');

      prefetchDebug('dashboard execute complete', {
        cacheEntries: queryClient
          .getQueryCache()
          .getAll()
          .map(q => ({
            keyHash: hashKey(q.queryKey),
            key: q.queryKey,
            fetchStatus: q.state.fetchStatus,
            status: q.state.status,
            dataUpdatedAt: q.state.dataUpdatedAt,
          })),
      });
    } catch (error) {
      mark('quickfetch:error', {
        errorType: error instanceof Error ? error.name : typeof error,
      });
      // Whole-quickfetch failure: fail open on the hidden-meetings gate (see
      // seedEmptyHiddenMeetingsIfUnseeded) so the dashboard's meetings query
      // isn't disabled forever — it fetches live instead.
      seedEmptyHiddenMeetingsIfUnseeded(queryClient, orgId);
      if (import.meta.env.DEV) {
        console.warn('[prefetch] Dashboard quickfetch failed:', error);
      }
      Sentry.captureException(error, {
        tags: {
          component: 'prefetch',
          page: 'dashboard',
          org_id: orgId.toString(),
        },
      });
    }
  },
};
